<!--
 * @Description: 线索与客户选择组件
 * @Author: mdb
 * @Date: 2022-08-16 11:28:56
 * @LastEditTime: 2023-03-09 15:16:31
 * @LastEditors: mdb
-->
<template>
  <CosViewInput v-if="view" :value="viewValue || modelValue" />

  <CosSelect
    v-else
    v-model:value="modelValue"
    showSearch
    :filterOption="false"
    :placeholder="t('common.pleaseSelect')"
    :disabled="props.disabled"
    :allowClear="false"
    @popupScroll="popupScroll"
    @focus="handleFocus"
    @search="handleSearch"
    clearVal=""
  >
    <template #dropdownRender="{ menuNode: menu }">
      <a-spin :spinning="searchLoading">
        <v-nodes :vnodes="menu" />
      </a-spin>
    </template>
    <a-select-option v-for="item in clueAndCustomerEnum" :key="item.id" :value="item.id" :disabled="item.disabled">
      {{ item.customerNumber ? `${item.customerName}(${item.customerNumber})` : item.customerName }}
    </a-select-option>
  </CosSelect>
</template>

<script setup lang="ts">
import { ref, watchEffect, watch, defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVModel, useDebounceFn } from '@vueuse/core'
import { getClueAndCustomerPage as clueAndCustomerFbu } from '@/api/gucangSales/chanceManage'
import { getClueAndCustomerPage as clueAndCustomerLbu, getCustomerPage } from '@/api/yuntuSales/chanceManage'
import { getClueAndCustomerPage as clueAndCustomerOfbu } from '@/api/overseasSales/chanceManage'
import CosViewInput from '@/components/Antd/CosView/input.vue'
const { t } = useI18n()
const getClueAndCustomerMethod = {
  cos_fbu: clueAndCustomerFbu,
  cos_lbu: clueAndCustomerLbu,
  cos_ofbu: clueAndCustomerOfbu
}

const VNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true
    }
  },
  render() {
    return this.vnodes
  }
})

const props = defineProps<{
  appId: 'cos_fbu' | 'cos_lbu' | 'cos_ofbu'
  value?: string | number
  label?: string
  record?: object
  disabled?: boolean
  view?: boolean
  onlyCustomer?: boolean
}>()
const emit = defineEmits(['update:value', 'update:label', 'update:record'])
const modelValue = useVModel(props, 'value', emit)
const modelLabel = useVModel(props, 'label', emit)
const modelRecord = useVModel(props, 'record', emit)
const searchLoading = ref(false) // 搜索框加载中状态
const clueAndCustomerEnum = ref<any[]>([]) // 客户下拉选择框数据
const searchValue = ref<string>() // 搜索框值
const initId = ref<number | string>() // 初始化id
const firstInit = ref(true) // 第一次初始化
const totalPage = ref(0) // 总页数
const viewValue = ref<string | number>('')
const queryForm = ref<{
  id?: number | string
  content?: string
  pageNumber: number
  pageSize: number
}>({ pageNumber: 1, pageSize: 100 }) // 分页参数
const getClueAndCustomerList = () => {
  (props.onlyCustomer ? getCustomerPage : getClueAndCustomerMethod[props.appId])(queryForm.value).then(({ data }) => {
    searchLoading.value = false
    if (queryForm.value.content) {
      clueAndCustomerEnum.value = []
      delete queryForm.value.content
    }
    // 1、带 id 参数请求时只会返回一条数据
    // 2、当后续返回参数包含第一条数据时,需要删除该条数据,防止数据重复
    const findIndex = data.items.findIndex((item: any) => item.id === initId.value)
    if (findIndex !== -1 && !firstInit.value) data.items.splice(findIndex, 1)

    // 1、初始化带 id 参数请求时,如果查询数据为空,则考虑是用户已被假如黑名单
    // 2、此时将详情数据的 id 和 name 暂时赋值给下拉选项,便于展示数据
    if (firstInit.value && !data.items.length && modelValue.value && modelLabel.value) {
      data.items.push({
        disabled: true,
        id: modelValue.value,
        customerName: modelLabel.value
      })
    }

    // TODO: bug 临时处理，当只有一个数据时，判断是否重复添加
    if (clueAndCustomerEnum.value.length === 1) {
      const findIndex = data.items.findIndex((item: any) => item.id === clueAndCustomerEnum.value[0].id)
      if (findIndex !== -1) data.items.splice(findIndex, 1)
    }

    // 分页数据合并
    clueAndCustomerEnum.value = clueAndCustomerEnum.value.concat(data.items || [])
    totalPage.value = data.totalPage
    // 当 disabled 为 false 时,修改 firstInit 状态，否则每次都当作是首次初始化
    if (!props.disabled) firstInit.value = false
  })
}

watch(
  modelValue,
  async (newValue, oldValue) => {
    if (newValue && firstInit.value) {
      // 数据回显首次初始化
      initId.value = newValue
      queryForm.value.id = newValue
      await getClueAndCustomerList()
      delete queryForm.value.id
    }
    // 监听 clear 清空
    if (newValue == null && oldValue) {
      clueAndCustomerEnum.value = []
      queryForm.value.pageNumber = 1
      await getClueAndCustomerList()
    }
  },
  { immediate: true }
)

watchEffect(() => {
  if (modelValue.value && clueAndCustomerEnum.value.length) {
    const find = clueAndCustomerEnum.value.find((item) => item.id === modelValue.value)
    modelLabel.value = find?.customerName
    modelRecord.value = find || {}
    if (find) {
      viewValue.value = find.customerNumber ? `${find.customerName}(${find.customerNumber})` : find.customerName
    } else {
      viewValue.value = modelValue.value
    }
  }
})

const popupScroll = (e: Event) => {
  const { scrollHeight, scrollTop, clientHeight } = e.target as HTMLDivElement
  if (scrollHeight - scrollTop === clientHeight && queryForm.value.pageNumber < totalPage.value) {
    queryForm.value.pageNumber++
    getClueAndCustomerList()
  }
}
const handleFocus = () => {
  if (searchValue.value != null) return
  if (clueAndCustomerEnum.value.length === 0 || clueAndCustomerEnum.value.length === 1) {
    getClueAndCustomerList()
  }
}
const handleSearch = useDebounceFn((val: string) => {
  searchValue.value = val.trim()
  searchLoading.value = true
  if (val) {
    queryForm.value.content = val.trim()
  } else {
    clueAndCustomerEnum.value = []
  }
  queryForm.value.pageNumber = 1
  delete queryForm.value.id
  getClueAndCustomerList()
}, 500)
</script>
<script lang="ts">
export default { name: 'ClueAndCustomerSelect' }
</script>

<style scoped></style>
